import React, { useState, useEffect, useMemo } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { fetchData, getPageHeader } from '../utils/apiServices';
import { CopyButton, LoadingSpinner, NotAllowed } from "./_Common"
import { ItemsList } from './ItemsList';

export function InstContactInfoFooter({ instDetails, isOneLine = false }) {
    const itemCss = "text-nowrap d-block d-md-inline-block mt-2 mt-md-0";
    const separator = <span className="mx-1 d-none d-md-inline">|</span>;
    return (
        <div className="text-center pb-3 no-print">
            <hr className="hrFooter mb-2" />
            <div className={isOneLine ? "d-flex justify-content-between align-items-center mx-3" : ""}>
                <p className="h3">{instDetails.description}</p>
                <span className={isOneLine ? "h4" : "h6"}>
                    <span className={itemCss}>
                        <FontAwesomeIcon icon="fas fa-location-dot" />&nbsp;
                        <a href={instDetails.googleMap} target="_blank" rel="noopener noreferrer">{instDetails.address}</a>
                    </span>
                    {separator}
                    <span className={itemCss}>
                        <FontAwesomeIcon icon="fas fa-phone-square" />&nbsp;
                        <a href={`tel:${instDetails.phones}`}>{instDetails.phones}</a>
                    </span>
                    {separator}
                    <span className={itemCss}>
                        <FontAwesomeIcon icon="fas fa-envelope" />&nbsp;
                        <a href={`mailto:${instDetails.email}`}>{instDetails.email}</a>
                    </span>
                </span>
            </div>
        </div>
    )
};

////InstContactInfo
const InstContactInfo = ({ instDetails }) => {
    const itemCss = "text-nowrap d-block d-md-inline-block mt-2 mt-md-0";
    const separator = <span className="mx-1 d-none d-md-inline">|</span>;
    return (
        <div className="text-center">
            <h5>
                <span className={itemCss}>
                    <FontAwesomeIcon icon="fas fa-location-dot" />
                    &nbsp;
                    <a href={instDetails.googleMap} target="_blank" rel="noopener noreferrer">{instDetails.address}</a>
                </span>
                {separator}
                <span className={itemCss}>
                    <FontAwesomeIcon icon="fas fa-phone-square" />
                    &nbsp;
                    <a href={`tel:${instDetails.phones}`}>{instDetails.phones}</a>
                </span>
                {separator}
                <span className={itemCss}>
                    <FontAwesomeIcon icon="fas fa-envelope" />
                    &nbsp;
                    <a href={`mailto:${instDetails.email}`}>{instDetails.email}</a>
                </span>
            </h5>
        </div>
    );
};

//// Contact Item Component
const ContactItem = ({ contact }) => (
    <div className="list-group-item d-flex flex-column flex-md-row justify-content-between align-items-start w-100 w-md-75 mx-auto">
        <div className="d-flex align-items-start gap-3">
            {contact.fullName && <div className="contact-avatar">
                <FontAwesomeIcon icon="fas fa-address-card" className='text-dark fs-3' />
            </div>}
            <div className="text-truncate">
                <div className="fw-bold text-dark m-0">{contact.fullName}</div>
                {contact.jobTitle && (
                    <small className="text-muted d-block text-truncate">{contact.jobTitle}</small>
                )}
            </div>
        </div>

        <div className="d-flex justify-content-end align-items-center align-self-end align-self-md-auto mt-2 mt-md-0">
            <CopyButton text={contact.email} iconClassSize="" tooltip="העתקת כתובת דוא&quot;ל" />

            <a href={`mailto:${contact.email}`} target="_blank" rel="noopener noreferrer" className="contact-email-btn flex-shrink-0">
                <span className="fs-6">{contact.email}</span>
                <FontAwesomeIcon icon="fas fa-envelope" className="fa-xl ms-1" />
            </a>
        </div>
    </div>
);

//// Render Function for ItemsList
const toHtmlElements = (contacts) => {
    let lastGroupId = null;

    return contacts.map((contact, index) => {
        const isNewGroup = contact.contactGroup !== lastGroupId;
        if (isNewGroup) {
            lastGroupId = contact.contactGroup;
        }

        const hasGroupName = contact.groupName && contact.groupName.trim() !== '';
        const showHeader = isNewGroup && (hasGroupName || index > 0);

        return (
            <React.Fragment key={contact.id || index}>
                {showHeader && (
                    <div className="contact-group-header">
                        {hasGroupName && (
                            <h4 className="ms-0 ms-md-5">{contact.groupName}</h4>
                        )}
                    </div>
                )}

                <ContactItem contact={contact} />
            </React.Fragment>
        );
    });
};

//// Main ContactPage Component
export default function ContactPage({ instDetails }) {
    const [loading, setLoading] = useState(true);
    const [notAlowed, setNotAlowed] = useState(false);
    const [pageHeader, setPageHeader] = useState(null);
    const [contacts, setContacts] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const [contactsRes, groupsRes, fetchedPageHeader] = await Promise.all([
                    fetchData('/api/Contacts'),
                    fetchData('/api/ContactGroupGroups'),
                    getPageHeader({ pageName: "Contacts" })
                ]);

                if (contactsRes.error === "Resource not found") {
                    setNotAlowed(true);
                } else {
                    const rawContacts = contactsRes.data || [];
                    const groups = groupsRes.data || [];

                    // Map group IDs to their priority and name
                    const groupMap = new Map();
                    groups.forEach((g) => {
                        const id = g.id ?? g.contactGroup ?? g.code;
                        groupMap.set(id, {
                            name: g.name || g.contactGroupName || '',
                            priority: g.priority ?? Infinity
                        });
                    });

                    // Transform & enrich contacts with group information and search string
                    const processedContacts = rawContacts.map((contact) => {
                        const groupVal = Number(contact.contactGroup) || 0;
                        const groupInfo = groupMap.get(groupVal);

                        const groupName = groupInfo?.name || contact.contactGroupName || '';
                        const groupPriority = groupVal > 0 ? (groupInfo?.priority ?? Infinity) : Infinity;

                        return {
                            ...contact,
                            groupVal,
                            groupName,
                            groupPriority,
                            textSearch: `${contact.jobTitle || ''} ${contact.fullName || ''} ${groupName}`
                        };
                    });

                    // Sort: contactGroup > 0 first (by Group Priority), then contactGroup === 0 at the end
                    processedContacts.sort((a, b) => {
                        const aHasGroup = a.groupVal > 0;
                        const bHasGroup = b.groupVal > 0;

                        // 1. Group > 0 comes before Group <= 0
                        if (aHasGroup !== bHasGroup) return aHasGroup ? -1 : 1;

                        // 2. Sort by Group Priority (for group > 0)
                        if (aHasGroup && a.groupPriority !== b.groupPriority) {
                            return a.groupPriority - b.groupPriority;
                        }

                        // 3. Keep same groups clustered
                        if (a.groupVal !== b.groupVal) {
                            return a.groupVal - b.groupVal;
                        }

                        // 4. Secondary sort by Contact Priority
                        return (a.priority ?? 0) - (b.priority ?? 0);
                    });

                    setContacts(processedContacts);
                }

                setPageHeader(fetchedPageHeader);
            } catch (err) {
                console.error("Failed to load contacts data:", err);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const msg = <InstContactInfo instDetails={instDetails} />;
    const header = pageHeader;

    if (loading) { return <LoadingSpinner />; }
    if (notAlowed) { return <NotAllowed />; }

    return (
        <ItemsList header={header} msg={msg} items={contacts} toHtml={toHtmlElements} liMoreCss="w-100 w-md-75 mx-auto" />
    );
}